import { z } from 'zod';
import { isIsoDate } from './pricing/dates.js';
import { pctOrAmountSchema, presentationSchema } from './pricing/schemas.js';
// Oferta dins la conversa d'una consulta (§6, §6.10, §8). L'agent la prepara amb el motor de preus,
// el servidor la calcula de nou i la congela, i el client l'accepta o la rebutja.
// Decisions de l'usuari:
// - validesa configurable per casa (per defecte 3 dies), ajustable a cada oferta, i pot no caducar;
// - mentre és vàlida, les dates queden provisionals;
// - en acceptar-la, segons el que hagi triat l'agent per a aquella oferta: reservades de seguida,
//   o provisionals fins que l'agent les confirmi.
// Decisió de l'usuari (2026-10-30): per al client no és una «oferta» (convidaria a regatejar), sinó
// el **pressupost final** de la seva estada. L'ajust de preu de l'agent (estades llargues, últim minut)
// no surt mai com a descompte: el client veu el preu agregat (`clientPresentation`).
export const DEFAULT_OFFER_VALIDITY_DAYS = 3;
export const OFFER_VALIDITY_CHOICES = [1, 2, 3, 7, 14, 30];
export const OFFER_NOTE_MAX = 500;
export const OFFER_STATUSES = ['sent', 'accepted', 'booked', 'declined', 'withdrawn'];
/** Què passa quan el client l'accepta. */
export const ON_ACCEPT = ['book', 'hold'];
const isoDate = z.string().refine(isIsoDate, 'invalidDate');
export const sendOfferInputSchema = z
    .object({
    inquiryId: z.string().min(1).max(40),
    checkIn: isoDate,
    checkOut: isoDate,
    taxablePersons: z.number().int().min(0).max(50),
    includeTouristTax: z.boolean(),
    includeVat: z.boolean(),
    /** Descompte global d'aquest lloguer (substitueix el de la casa). null: cap. */
    globalDiscount: pctOrAmountSchema.nullable(),
    presentation: presentationSchema,
    /** Dies de validesa; null: no caduca. */
    validityDays: z.number().int().min(1).max(60).nullable(),
    onAccept: z.enum(ON_ACCEPT),
    note: z.string().trim().max(OFFER_NOTE_MAX),
})
    .refine((o) => o.checkOut > o.checkIn, { message: 'order', path: ['checkOut'] });
export const respondOfferInputSchema = z.object({
    inquiryId: z.string().min(1).max(40),
    offerId: z.string().min(1).max(40),
    action: z.enum(['accept', 'decline']),
});
export const manageOfferInputSchema = z.object({
    inquiryId: z.string().min(1).max(40),
    offerId: z.string().min(1).max(40),
    action: z.enum(['withdraw', 'book']),
});
/** Caducada: enviada, amb data límit i passada. Les acceptades, reservades o tancades no caduquen. */
export function offerExpired(o, nowIso) {
    return o.status === 'sent' && o.validUntil !== null && o.validUntil <= nowIso;
}
/** Data límit d'una oferta enviada ara. */
export function offerValidUntil(now, validityDays) {
    return validityDays === null ? null : new Date(now.getTime() + validityDays * 86_400_000).toISOString();
}
/** Com veu el client el pressupost: sempre agregat, sense desglossar el lloguer ni els ajustos. */
export function clientPresentation(p) {
    return { ...p, detail: 'aggregate' };
}
